import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { FileText, Tag, Bell, Truck, CheckCircle, ExternalLink, Code2, Warehouse, MapPin, ArrowRight, Download } from 'lucide-react';
import { PREALERT_URL } from '../lib/links';

// Receiving rules per US warehouse (labels live in process.usOps)
const US_WAREHOUSE_OPS = [
  { id: 'miami', name: 'Miami', hub: 'MIA', delivery: 'both', bol: 'own', dg: 'yes', notes: 'miami' },
  { id: 'laredo', name: 'Laredo', hub: 'LRD', delivery: 'both', bol: 'own', dg: 'no', notes: 'laredo' },
  { id: 'chicago', name: 'Chicago', hub: 'ORD', delivery: 'carrierOnly', bol: 'carrier', dg: 'no', notes: 'carrier' },
  { id: 'newjersey', name: 'New Jersey', hub: 'EWR', delivery: 'carrierOnly', bol: 'carrier', dg: 'no', notes: 'carrier' },
  { id: 'losangeles', name: 'Los Angeles', hub: 'LAX', delivery: 'carrierOnly', bol: 'carrier', dg: 'no', notes: 'carrier' }
];

const deliveryClass = (mode) =>
  mode === 'carrierOnly'
    ? 'bg-orange-50 text-orange-700 border-orange-200'
    : 'bg-[#7AC142]/10 text-[#4d7f24] border-[#7AC142]/30';

// Jumps to the warehouse card in NetworkSection (listens for 'focusWarehouse')
const WarehouseLink = ({ id, label, className = '', testIdSuffix = '' }) => (
  <button
    type="button"
    onClick={() => window.dispatchEvent(new CustomEvent('focusWarehouse', { detail: id }))}
    className={`group inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-bold text-black border-b-2 border-[#7AC142] pb-0.5 hover:text-[#4d7f24] transition-colors ${className}`}
    data-testid={`warehouse-link-${id}${testIdSuffix}`}
  >
    <MapPin className="w-3.5 h-3.5 text-[#7AC142]" />
    {label}
    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
  </button>
);

const UsWarehouseOps = ({ t }) => {
  const cols = ['delivery', 'bol', 'dg', 'notes'];
  const cell = (w, col) => t(`process.usOps.${col}.${w[col]}`);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mt-20"
      data-testid="us-warehouse-ops"
    >
      <div className="mb-6">
        <span className="eyebrow">
          <Warehouse className="w-4 h-4" />
          {t('process.usOps.label')}
        </span>
        <h3 className="mt-3 text-2xl md:text-3xl font-extrabold text-black tracking-tight">{t('process.usOps.title')}</h3>
        <p className="mt-2 text-gray-600">{t('process.usOps.subtitle')}</p>
      </div>

      {/* Desktop: comparison table */}
      <div className="hidden md:block border-2 border-black bg-white">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-black text-white">
              <th className="px-4 py-3 w-[230px] text-[11px] font-mono font-bold uppercase tracking-wider">{t('process.usOps.cols.warehouse')}</th>
              {cols.map((c) => (
                <th key={c} className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-wider">{t(`process.usOps.cols.${c}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {US_WAREHOUSE_OPS.map((w) => (
              <tr key={w.hub} className="border-t border-gray-200 align-top">
                <td className="px-4 py-4">
                  <span className="font-bold text-black">{w.name}</span>
                  <span className="ml-2 text-[10px] font-mono font-bold text-gray-500">{w.hub}</span>
                  <div className="mt-2">
                    <WarehouseLink id={w.id} label={t('process.usOps.cta')} />
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-block px-2 py-1 text-xs font-semibold border ${deliveryClass(w.delivery)}`}>
                    {cell(w, 'delivery')}
                  </span>
                </td>
                <td className="px-4 py-4 text-gray-700">{cell(w, 'bol')}</td>
                <td className={`px-4 py-4 ${w.dg === 'yes' ? 'font-semibold text-black' : 'text-gray-500'}`}>{cell(w, 'dg')}</td>
                <td className="px-4 py-4 text-gray-700">{cell(w, 'notes')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: one card per warehouse */}
      <div className="md:hidden space-y-3">
        {US_WAREHOUSE_OPS.map((w) => (
          <div key={w.hub} className="border-2 border-black bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="font-bold text-black">{w.name}</span>
              <span className="text-[10px] font-mono font-bold text-gray-500">{w.hub}</span>
            </div>
            <span className={`mt-3 inline-block px-2 py-1 text-xs font-semibold border ${deliveryClass(w.delivery)}`}>
              {cell(w, 'delivery')}
            </span>
            <dl className="mt-3 space-y-2 text-sm">
              {['bol', 'dg', 'notes'].map((c) => (
                <div key={c}>
                  <dt className="text-[10px] font-mono font-bold uppercase tracking-wider text-gray-500">{t(`process.usOps.cols.${c}`)}</dt>
                  <dd className="text-gray-700">{cell(w, c)}</dd>
                </div>
              ))}
            </dl>
            <WarehouseLink id={w.id} label={t('process.usOps.cta')} className="mt-4" testIdSuffix="-mobile" />
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const ProcessSection = () => {
  const { t } = useLanguage();

  const steps = [
    {
      key: 'tsa',
      icon: FileText,
      number: '01',
      link: 'https://docs.google.com/forms/d/e/1FAIpQLSc0KbUTDi7nlAst-hTEWmDx-5LpTucUqQwf757D9owanjF4Rg/viewform'
    },
    {
      key: 'labels',
      icon: Tag,
      number: '02',
      apiAvailable: true
    },
    {
      key: 'prealert',
      icon: Bell,
      number: '03',
      link: PREALERT_URL,
      apiAvailable: true
    },
    {
      key: 'delivery',
      icon: Truck,
      number: '04'
    },
    {
      key: 'confirmation',
      icon: CheckCircle,
      number: '05'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section
      id="process"
      data-testid="process-section"
      className="py-24 md:py-32 bg-white grid-bg relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="eyebrow mb-4">
            {t('process.label')}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight">
            {t('process.title')}
            <br />
            <span className="text-[#7AC142]">{t('process.titleAccent')}</span>
          </h2>
        </motion.div>

        {/* Process timeline - Desktop horizontal */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="hidden lg:flex justify-between items-stretch relative"
        >
          {/* Connector line */}
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-gray-200">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: '100%' }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, delay: 0.5 }}
              className="h-full bg-[#7AC142]"
            />
          </div>

          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.key}
                variants={itemVariants}
                className="relative flex flex-col items-center text-center w-1/5 px-3"
              >
                {/* Step number and icon */}
                <div className="relative z-10 bg-white p-2">
                  <div className="w-16 h-16 border-2 border-black flex items-center justify-center group hover:bg-[#7AC142] hover:border-[#7AC142] transition-all cursor-pointer">
                    <Icon className="w-7 h-7 text-black group-hover:text-white transition-colors" />
                  </div>
                </div>

                {/* Step number */}
                <span className="mt-4 text-xs font-mono text-[#7AC142] tracking-wider">
                  {t('process.step')} {step.number}
                </span>

                {/* Title */}
                <h3 className="mt-2 text-lg font-bold text-black tracking-tight">
                  {t(`process.steps.${step.key}.title`)}
                </h3>

                {/* Subtitle */}
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mt-1">
                  {t(`process.steps.${step.key}.subtitle`)}
                </p>

                {/* Description */}
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                  {t(`process.steps.${step.key}.description`)}
                </p>

                {/* Actions pinned to the bottom so they line up across steps */}
                {(step.link || step.download || step.apiAvailable) && (
                  <div className="mt-auto pt-4 flex flex-col items-center gap-2">
                    {step.link && (
                      <a
                        href={step.link}
                        target={step.link.startsWith('http') ? '_blank' : '_self'}
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#7AC142] text-sm font-medium hover:underline"
                        data-testid={`process-link-${step.key}`}
                      >
                        {t('process.clickHere')}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}

                    {/* Downloadable template if available */}
                    {step.download && (
                      <a
                        href={step.download}
                        download
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#7AC142] text-black text-[10px] font-bold uppercase tracking-wider hover:bg-[#69A835] transition-colors"
                        data-testid={`process-download-${step.key}`}
                      >
                        <Download className="w-3 h-3" />
                        {t('process.downloadTemplate')}
                      </a>
                    )}

                    {/* API available badge */}
                    {step.apiAvailable && (
                      <button
                        onClick={() => document.getElementById('api-docs')?.scrollIntoView({ behavior: 'smooth' })}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black text-[#7AC142] text-[10px] font-mono font-bold uppercase tracking-wider border border-[#7AC142]/40 hover:bg-[#7AC142] hover:text-black transition-colors"
                        data-testid={`api-badge-${step.key}`}
                      >
                        <Code2 className="w-3 h-3" />
                        {t('process.apiAvailable')}
                      </button>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>

        {/* Process timeline - Mobile/Tablet vertical */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="lg:hidden space-y-0"
        >
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.key}
                variants={itemVariants}
                className="relative pl-20 pb-12 last:pb-0"
              >
                {/* Vertical line */}
                {index < steps.length - 1 && (
                  <div className="absolute left-8 top-16 bottom-0 w-0.5 bg-gradient-to-b from-[#7AC142] to-gray-200" />
                )}

                {/* Icon */}
                <div className="absolute left-0 top-0 w-16 h-16 border-2 border-black flex items-center justify-center bg-white">
                  <Icon className="w-7 h-7 text-black" />
                </div>

                {/* Content */}
                <div className="border-l-4 border-gray-200 pl-6 hover:border-[#7AC142] transition-colors">
                  <span className="text-xs font-mono text-[#7AC142] tracking-wider">
                    {t('process.step')} {step.number}
                  </span>
                  <h3 className="mt-1 text-xl font-bold text-black tracking-tight">
                    {t(`process.steps.${step.key}.title`)}
                  </h3>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mt-1">
                    {t(`process.steps.${step.key}.subtitle`)}
                  </p>
                  <p className="mt-3 text-gray-600 leading-relaxed">
                    {t(`process.steps.${step.key}.description`)}
                  </p>
                  {step.link && (
                    <a
                      href={step.link}
                      target={step.link.startsWith('http') ? '_blank' : '_self'}
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-1 text-[#7AC142] font-medium hover:underline"
                    >
                      {t('process.clickHere')}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  {step.download && (
                    <a
                      href={step.download}
                      download
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-[#7AC142] text-black text-[11px] font-bold uppercase tracking-wider hover:bg-[#69A835] transition-colors"
                      data-testid={`process-download-mobile-${step.key}`}
                    >
                      <Download className="w-4 h-4" />
                      {t('process.downloadTemplate')}
                    </a>
                  )}
                  {step.apiAvailable && (
                    <button
                      onClick={() => document.getElementById('api-docs')?.scrollIntoView({ behavior: 'smooth' })}
                      className={`mt-3 ${step.link ? 'ml-3' : ''} inline-flex items-center gap-1.5 px-2.5 py-1 bg-black text-[#7AC142] text-[10px] font-mono font-bold uppercase tracking-wider border border-[#7AC142]/40 hover:bg-[#7AC142] hover:text-black transition-colors`}
                    >
                      <Code2 className="w-3 h-3" />
                      {t('process.apiAvailable')}
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <UsWarehouseOps t={t} />
      </div>
    </section>
  );
};

export default ProcessSection;
